import { ContentChildren, Directive, EventEmitter, HostBinding, HostListener, Input, Output, } from '@angular/core';
import * as i0 from "@angular/core";
import * as i1 from "./drag-and-drop.service";
export class DraggableDirective {
    constructor(ngZone, elementRef, renderer, droppableDraggableService) {
        this.ngZone = ngZone;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.droppableDraggableService = droppableDraggableService;
        this.enabled = true;
        // number of pixels up/down an element has shifted
        this.shiftedY = new EventEmitter();
        this.stylePrefixes = ['', '-moz-', '-ms-', '-o-', '-webkit-'];
        this.parentContainer = droppableDraggableService.getParentContainer();
        this.isMoving = false;
        this.activeZIndex = 10000;
        this.inactiveZIndex = this.elementRef.nativeElement.style.zIndex;
        this.isPositionSet = !!this.elementRef.nativeElement.style.position;
        this.droppableDraggableService.onParentScroll$.subscribe(() => {
            if (!this.isMoving) {
                return false;
            }
            this.setParentScrollOffset();
            this.doDrag();
            return true;
        });
        this.renderer.addClass(this.elementRef.nativeElement, 'mheDraggable');
        this.ngZone.runOutsideAngular(() => {
            // The 'mousemove' event fires for every pixel.
            // Running it outside of Angular prevents change detection from overloading the browser.
            window.addEventListener('mousemove', (event) => {
                this.handleWindowMouseMove(event);
                return true;
            });
            // Similar to mousemove, there is no change happening that Angular needs to know about.
            window.addEventListener('scroll', (event) => {
                this.setWindowScrollOffset(event);
                return true;
            });
        });
    }
    handleDragStart(event) {
        if (!this.enabled) {
            return false;
        }
        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        this.initialStats = {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        };
        this.parentScrollStart = {
            x: this.parentContainer.elementRef.nativeElement.scrollLeft,
            y: this.parentContainer.elementRef.nativeElement.scrollTop,
        };
        this.windowScrollStart = {
            x: window.pageXOffset,
            y: window.pageYOffset,
        };
        this.setParentScrollOffset();
        this.setWindowScrollOffset(null);
        // needed to calculate mouse offset to ensure the tile is moving the proper distance
        this.mouseStart = {
            x: event.clientX,
            y: event.clientY,
        };
        this.mouseMove = {
            x: 0,
            y: 0,
        };
        if (!this.isPositionSet) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');
        }
        this.renderer.setStyle(this.elementRef.nativeElement, 'z-index', this.activeZIndex);
        this.isMoving = true;
        this.removeBrowserSafeStyle('transition');
        this.droppableDraggableService.emitDragStart({
            startIndex: this.elementIndex,
        });
        event.stopPropagation();
        return false;
    }
    handleWindowMouseMove(event) {
        if (!this.isMoving) {
            return true;
        }
        this.mouseMove = {
            x: event.clientX - this.mouseStart.x,
            y: event.clientY - this.mouseStart.y,
        };
        this.doDrag();
        return true;
    }
    doDrag() {
        const translationVector = {
            x: this.restrictMovementX ? 0 : this.mouseMove.x + this.scrollOffset.x + this.windowOffset.x,
            y: this.restrictMovementY ? 0 : this.mouseMove.y + this.scrollOffset.y + this.windowOffset.y,
        };
        this.setBrowserSafeStyle('transform', `translate(${translationVector.x}px, ${translationVector.y}px)`);
        // I don't have access to the most recent mouse event, but I can derive it using available values.
        this.droppableDraggableService.emitDragging({
            clientX: this.mouseMove.x + this.mouseStart.x,
            clientY: this.mouseMove.y + this.mouseStart.y,
            currDraggable: this,
        });
    }
    setWindowScrollOffset(event) {
        this.windowOffset = {
            x: window.pageXOffset - this.windowScrollStart.x,
            y: window.pageYOffset - this.windowScrollStart.y,
        };
    }
    resetTransform() {
        // const endIndex = this.elementIndex + this.shifts;
        const endIndex = this.getShiftIndex();
        // 'any' used here becausee typescript linter doesn't truly know of "NodeList" type.
        // https://developer.mozilla.org/en-US/docs/Web/API/NodeList
        this.removeBrowserSafeStyle('transform');
        this.removeBrowserSafeStyle('transition');
        if (!this.isPositionSet) {
            this.renderer.removeStyle(this.elementRef.nativeElement, 'position');
        }
        if (endIndex !== this.elementIndex && this.isMoving) {
            this.droppableDraggableService.emitDragEnd({
                startIndex: this.elementIndex,
                endIndex,
            });
        }
        this.renderer.setStyle(this.elementRef.nativeElement, 'z-index', this.inactiveZIndex);
        this.isMoving = false;
        this.shifts = 0;
        this.shiftedY.emit(0);
    }
    ngOnInit() {
        this.windowScrollStart = { x: 0, y: 0 };
        this.parentScrollStart = { x: 0, y: 0 };
        this.setParentScrollOffset();
        this.setWindowScrollOffset(null);
    }
    ngAfterViewInit() {
        this.resetTransform();
        this.droppableDraggableService.registerDraggable(this);
    }
    getShiftIndex() {
        return this.shifts + this.elementIndex;
    }
    shiftUp() {
        if (this.shifts < this.shiftMax) {
            this.shifts++;
        }
        this.shift();
    }
    shiftDown() {
        if (this.shifts > this.shiftMin) {
            this.shifts--;
        }
        this.shift();
    }
    shift() {
        if (this.isMoving) {
            return false;
        }
        this.setBrowserSafeStyle('transform', `translate(${this.shifts * this.translationVector.x}px, ${this.shifts * this.translationVector.y}px)`);
        const yTransform = this.shifts * this.translationVector.y;
        this.shiftedY.emit(yTransform);
        return true;
    }
    setParentScrollOffset() {
        if (!this.parentContainer) {
            this.scrollOffset = {
                x: 0,
                y: 0,
            };
            return;
        }
        this.scrollOffset = {
            x: this.parentContainer.elementRef.nativeElement.scrollLeft - this.parentScrollStart.x,
            y: this.parentContainer.elementRef.nativeElement.scrollTop - this.parentScrollStart.y,
        };
    }
    setBrowserSafeStyle(property, style) {
        this.stylePrefixes.forEach((prefix) => {
            this.renderer.setStyle(this.elementRef.nativeElement, `${prefix}${property}`, style);
        });
    }
    removeBrowserSafeStyle(property) {
        this.stylePrefixes.forEach((prefix) => {
            this.renderer.removeStyle(this.elementRef.nativeElement, `${prefix}${property}`);
        });
    }
    setTransformVector(translationVector) {
        this.translationVector = translationVector;
    }
    setShiftMinMax(min, max) {
        this.shiftMin = min;
        this.shiftMax = max;
    }
    getNestedDraggables() {
        const draggables = [];
        this.draggables.forEach((child) => {
            draggables.push(child);
            draggables.splice(draggables.length, 0, ...child.getNestedDraggables());
        });
        return draggables;
    }
    setElementIndex(newIndex) {
        this.elementIndex = newIndex;
    }
    getElementIndex() {
        return this.elementIndex;
    }
    ngOnDestroy() {
        this.droppableDraggableService.deregisterDraggable(this);
    }
}
DraggableDirective.ɵfac = function DraggableDirective_Factory(t) { return new (t || DraggableDirective)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1.DragAndDropService)); };
DraggableDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: DraggableDirective, selectors: [["", "mheDraggable", ""]], contentQueries: function DraggableDirective_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, DraggableDirective, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.draggables = _t);
    } }, hostVars: 3, hostBindings: function DraggableDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("dragstart", function DraggableDirective_dragstart_HostBindingHandler($event) { return ctx.handleDragStart($event); });
    } if (rf & 2) {
        i0.ɵɵattribute("draggable", ctx.enabled);
        i0.ɵɵclassProp("dragging", ctx.isMoving);
    } }, inputs: { enabled: "enabled", restrictMovementX: "restrictMovementX", restrictMovementY: "restrictMovementY" }, outputs: { shiftedY: "shiftedY" } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DraggableDirective, [{
        type: Directive,
        args: [{
                selector: '[mheDraggable]',
            }]
    }], function () { return [{ type: i0.NgZone }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.DragAndDropService }]; }, { draggables: [{
            type: ContentChildren,
            args: [DraggableDirective, { descendants: true }]
        }], enabled: [{
            type: HostBinding,
            args: ['attr.draggable']
        }, {
            type: Input
        }], restrictMovementX: [{
            type: Input
        }], restrictMovementY: [{
            type: Input
        }], shiftedY: [{
            type: Output
        }], isMoving: [{
            type: HostBinding,
            args: ['class.dragging']
        }], handleDragStart: [{
            type: HostListener,
            args: ['dragstart', ['$event']]
        }] }); })();
